import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent, dialogSheetHostClass, DIALOG_SHEET_CAPPED_HOST } from '@lite-workspace/shared/helm-ui/dialog';
import { HlmShareLinkField } from '@lite-workspace/shared/helm-ui/share-link-field';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import { hlm } from '@lite-workspace/shared/helm-ui/utils';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { longRuDateText } from '@lite-workspace/shared/shared-public';
import { GalleriesStore } from '../../features/galleries/domain/+state/galleries.store';
import {
  GalleryDesignApiService,
  type GalleryDesignDTO,
} from '../../features/gallery-design/domain/infrastructure/gallery-design-api.service';
import { shareAddressOf } from '../../features/gallery-share/domain/share-address';
import {
  GalleryShareApiService,
  type GalleryShareAccessDTO,
} from '../../features/gallery-share/domain/infrastructure/gallery-share-api.service';

export interface ShareGalleryContext {
  galleryId: string;
}

type Phase = 'loading' | 'error' | 'ready';

// Send window «Открытка» (GAL-149; was «Поделиться», spec §1–9, rework E1–E4): the one window the photographer
// copies the gallery address from. It replaced the «Доступ» page as the entry to sharing — QR, email invites,
// the attempt limit and «Обновить ссылки» deliberately live nowhere here.
@Component({
  selector: 'gallery-share-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, HlmTooltip, HlmShareLinkField, IconComponent, DialogSheetComponent],
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <!-- «Открытка» = the approved GAL-149 T11.3 postcard mock: design coverImageUrl at coverFocus, name + shootDate. -->
        <div class="relative h-67.5 overflow-hidden bg-stage-black text-on-media">
          @if (cover()?.photoUrl; as url) {
            <img [src]="url" alt="" class="absolute inset-0 size-full object-cover" [style.object-position]="cover()?.focus" />
          }
          <div class="absolute inset-0 bg-linear-to-b from-transparent from-30% to-[var(--scrim-modal)]"></div>
          <div class="absolute inset-0 flex flex-col items-center justify-center gap-2.5 p-6 text-center">
            <h2
              class="text-3xl leading-display font-semibold tracking-[var(--ls-tight)]
                     [text-shadow:var(--shadow-on-media-title)]"
            >
              {{ data()?.title }}
            </h2>
            @if (cover()?.date; as date) {
              <p class="text-control tracking-[var(--ls-caps-l)] uppercase [text-shadow:var(--shadow-on-media-text)]">
                {{ date }}
              </p>
            }
          </div>
          <button
            type="button"
            (click)="dialogRef.close()"
            aria-label="Закрыть"
            class="absolute top-3.5 right-3.5 grid size-9 place-items-center rounded-full bg-[var(--wash-on-dark)] text-on-media
                   opacity-55 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
          >
            <app-icon name="close" [size]="20" />
          </button>
        </div>

        <div class="px-10 pt-8 pb-10">
          <!-- Height = content: loading holds one field, password rows grow the card when they arrive. -->
          <div class="flex flex-col gap-5">
            @switch (phase()) {
              @case ('loading') {
                <hlm-share-link-field label="Адрес галереи" [value]="null" />
              }
              @case ('error') {
                <p class="text-base text-bad">Не удалось загрузить ссылку. Попробуйте ещё раз.</p>
              }
              @default {
                @if (!live()) {
                  <!-- E4: the disabled gallery states the block and offers the one move that unblocks it. -->
                  <div class="flex items-start gap-3 rounded-[var(--r-l)] border border-bad-line bg-bad-soft px-4 py-3.5">
                    <span class="grid size-9 shrink-0 place-items-center rounded-[var(--r-l)] bg-bad-soft text-bad">
                      <app-icon name="lock-close" [size]="18" />
                    </span>
                    <p class="text-base leading-normal text-c2">Галерея выключена. Чтобы поделиться, включите её</p>
                  </div>
                  @if (enableError(); as err) {
                    <p class="text-base text-bad" role="alert">{{ err }}</p>
                  }
                } @else {
                  @if (address(); as url) {
                    <hlm-share-link-field
                      label="Адрес галереи"
                      [value]="url"
                      copyLabel="Скопировать ссылку на галерею"
                      copiedLabel="Ссылка скопирована"
                    />
                  }

                  <!-- E1: a password that is not set has no row at all — nothing to copy, nothing to explain. -->
                  @for (row of passwordRows(); track row.role) {
                    <hlm-share-link-field
                      [label]="row.label"
                      [value]="row.value"
                      [copyLabel]="row.copyLabel"
                      copiedLabel="Пароль скопирован"
                    >
                      <span
                        hint
                        class="-ml-0.25 grid size-7.5 shrink-0 cursor-help place-items-center rounded-full text-c2 hover:text-ink"
                        tabindex="0"
                        [attr.aria-label]="row.hintLabel"
                        [hlmTooltip]="row.hint"
                        position="top"
                      >
                        <app-icon name="help-circle" [size]="16" />
                      </span>
                    </hlm-share-link-field>
                  }
                }
              }
            }
          </div>

          <div class="mt-7.5 flex flex-col gap-2.5">
            @if (phase() === 'ready' && !live()) {
              <button hlmBtn size="dialog" type="button" class="text-base" [disabled]="enabling()" (click)="enable()">
                Включить галерею
              </button>
            } @else {
              <a
                hlmBtn
                variant="outline"
                size="dialog"
                class="text-base"
                [class.pointer-events-none]="!address()"
                [attr.aria-disabled]="address() ? null : true"
                [attr.tabindex]="address() ? null : -1"
                [class.opacity-50]="!address()"
                [href]="address() ?? ''"
                target="_blank"
                rel="noopener"
              >
                <app-icon name="open-external" [size]="18" />
                Посмотреть
              </a>
            }
          </div>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class ShareGalleryDialogComponent {
  // The cover runs edge to edge, so the card drops its padding (the pane below carries it) and clips the cover's corners.
  protected readonly hostClass = hlm(
    dialogSheetHostClass('fluid', 'p-9'),
    'dlg-desktop:w-115 dlg-desktop:p-0 dlg-desktop:overflow-hidden',
    DIALOG_SHEET_CAPPED_HOST,
  );

  protected readonly dialogRef = inject(BrnDialogRef);
  protected readonly ctx = injectBrnDialogContext<ShareGalleryContext>();
  private readonly api = inject(GalleryShareApiService);
  private readonly designApi = inject(GalleryDesignApiService);
  private readonly galleries = inject(GalleriesStore);

  protected readonly phase = signal<Phase>('loading');
  protected readonly data = signal<GalleryShareAccessDTO | null>(null);
  private readonly design = signal<GalleryDesignDTO | null>(null);

  protected readonly cover = computed(() => {
    const d = this.design();
    if (!d) return null;
    const shot = d.shootDate ? new Date(d.shootDate) : null;
    return {
      photoUrl: d.coverImageUrl,
      focus: d.coverFocus,
      date: shot && !Number.isNaN(shot.getTime()) ? longRuDateText(shot) : '',
    };
  });

  protected readonly enabling = signal(false);
  protected readonly enableError = signal<string | null>(null);

  protected readonly live = computed(() => this.data()?.live !== false);

  protected readonly address = computed(() => shareAddressOf(this.data()));

  /** Only the codes the photographer can actually pass on — an unset password has no row (E1). */
  protected readonly passwordRows = computed(() => {
    const d = this.data();
    return [
      {
        role: 'guest' as const,
        label: 'Пароль на галерею',
        // Parked code stays in the DTO for redisplay — showing it would promise a password the link does not ask for.
        value: d?.guestPasscodeEnabled ? (d.guestPasscode ?? null) : null,
        copyLabel: 'Скопировать пароль на галерею',
        hintLabel: 'Как настроить пароль на галерею',
        hint: 'Пароль спросят при входе в галерею — изменить его можно в настройках',
      },
      {
        role: 'client' as const,
        label: 'Пароль для клиента',
        value: d?.clientPasscode ?? null,
        copyLabel: 'Скопировать пароль для клиента',
        hintLabel: 'Как настроить пароль для клиента',
        hint: 'По нему клиент входит в свой отдельный доступ — изменить его можно в настройках',
      },
    ].filter((row): row is typeof row & { value: string } => !!row.value);
  });

  constructor() {
    void this.load(this.designApi.getDesign(this.ctx.galleryId));
  }

  /** The first read also awaits the cover, so the photo never pops in after the address — one render for both. */
  private async load(design?: Promise<GalleryDesignDTO>): Promise<void> {
    const [access, cover] = await Promise.allSettled([this.api.getShareAccess(this.ctx.galleryId), design]);
    // The cover is decoration: without it the window still shares, over the plain dark plate.
    if (cover.status === 'fulfilled' && cover.value) this.design.set(cover.value);
    if (access.status === 'fulfilled') {
      this.data.set(access.value);
      this.phase.set('ready');
    } else {
      this.phase.set('error');
    }
  }

  /** E4: enable without a confirmation step and re-read the share state in place — the window
   *  re-renders into «поделиться», it is never closed and reopened. The write goes through the
   *  galleries store — the one door for status — so the list row behind this window flips with it. */
  protected async enable(): Promise<void> {
    if (this.enabling()) return;
    this.enabling.set(true);
    this.enableError.set(null);
    const failure = await this.galleries.setStatus(this.ctx.galleryId, true);
    if (failure) {
      this.enableError.set(failure === 'no-access' ? 'Нет доступа' : 'Не удалось включить галерею');
      this.enabling.set(false);
      return;
    }

    await this.load();
    this.enabling.set(false);
  }
}
